import React, { useMemo, useState } from "react";
import { Button, Drawer } from "@mui/material";
import jsPDF from "jspdf";
import { investmentGlossary } from "./investorLibraryData";

const categories = ["All", ...new Set(investmentGlossary.map((item) => item.category))];

function downloadInvestorLibrary() {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const margin = 54;
  const ink = [20, 46, 44];
  const lime = [198, 241, 60];
  const muted = [96, 112, 107];
  const soft = [244, 247, 241];

  doc.setFillColor(...ink);
  doc.rect(0, 0, width, height, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(30);
  doc.text("PaiyHub", margin, 92);
  doc.setFillColor(...lime);
  doc.rect(margin, 118, 72, 7, "F");
  doc.setTextColor(...lime);
  doc.setFontSize(16);
  doc.text("INVESTOR LIBRARY", margin, 250);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(34);
  doc.text("Private-market terms,", margin, 320);
  doc.text("made practical.", margin, 362);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(13);
  doc.text(`${investmentGlossary.length} venture capital, fund operations, performance,`, margin, 430);
  doc.text("valuation, governance, and investor definitions.", margin, 450);
  doc.text("Each term includes an applied example scenario.", margin, 488);

  let y = 0;
  const startPage = () => {
    doc.addPage();
    doc.setTextColor(...ink);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text("Investor Library", margin, 54);
    doc.setDrawColor(...lime);
    doc.setLineWidth(2);
    doc.line(margin, 68, width - margin, 68);
    y = 94;
  };
  startPage();

  investmentGlossary.forEach((item) => {
    const definitionLines = doc.splitTextToSize(item.definition || "", width - margin * 2);
    const aliasLines = item.aliases ? doc.splitTextToSize(item.aliases, width - margin * 2 - 120) : [];
    const formulaLines = item.formula ? doc.splitTextToSize(item.formula, width - margin * 2 - 24) : [];
    const exampleLines = doc.splitTextToSize(item.example || "", width - margin * 2 - 24);
    const blockHeight = 56 + definitionLines.length * 13 + aliasLines.length * 11 + (formulaLines.length ? 22 + formulaLines.length * 12 : 0) + 27 + exampleLines.length * 13 + (item.sourceUrl ? 18 : 0);
    if (y + blockHeight > height - 52) startPage();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...ink);
    doc.text(item.term, margin, y);
    doc.setFontSize(8);
    doc.setTextColor(...muted);
    doc.text(item.category, width - margin, y, { align: "right" });
    y += 15;
    if (aliasLines.length) {
      doc.setTextColor(102, 135, 16);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text(aliasLines, margin, y);
      y += aliasLines.length * 11 + 5;
    }
    doc.setTextColor(...muted);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(definitionLines, margin, y);
    y += definitionLines.length * 13 + 8;
    if (formulaLines.length) {
      const formulaHeight = formulaLines.length * 12 + 16;
      doc.setFillColor(238, 246, 217);
      doc.roundedRect(margin, y, width - margin * 2, formulaHeight, 5, 5, "F");
      doc.setTextColor(64, 86, 25);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text(formulaLines, margin + 12, y + 14);
      y += formulaHeight + 8;
    }
    const exampleHeight = exampleLines.length * 13 + 29;
    doc.setFillColor(...soft);
    doc.roundedRect(margin, y, width - margin * 2, exampleHeight, 5, 5, "F");
    doc.setDrawColor(...lime);
    doc.setLineWidth(2);
    doc.line(margin + 1, y, margin + 1, y + exampleHeight);
    doc.setTextColor(...muted);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.text("EXAMPLE SCENARIO", margin + 12, y + 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(exampleLines, margin + 12, y + 28);
    y += exampleHeight + 7;
    if (item.sourceUrl) {
      doc.setTextColor(31, 102, 160);
      doc.setFontSize(8);
      doc.textWithLink("Source: Investopedia", margin, y + 3, { url: item.sourceUrl });
      y += 17;
    }
    doc.setDrawColor(224, 229, 223);
    doc.setLineWidth(0.5);
    doc.line(margin, y, width - margin, y);
    y += 18;
  });

  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(page === 1 ? 255 : muted[0], page === 1 ? 255 : muted[1], page === 1 ? 255 : muted[2]);
    doc.text("Investor Library - PaiyHub (c) 2026", margin, height - 24);
    doc.text(`${page} / ${totalPages}`, width - margin, height - 24, { align: "right" });
  }
  doc.save("PaiyHub-Investor-Library-2026.pdf");
}

export default function InvestorLibrary({ open, onClose }) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const filteredTerms = useMemo(() => {
    const query = search.trim().toLowerCase();
    return investmentGlossary.filter((item) => {
      const matchesCategory = category === "All" || item.category === category;
      const haystack = [item.term, item.aliases, item.category, item.definition, item.formula, item.example].filter(Boolean).join(" ").toLowerCase();
      return matchesCategory && (!query || haystack.includes(query));
    });
  }, [category, search]);

  const clearFilters = () => { setSearch(""); setCategory("All"); };
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ className: "xp-library-drawer" }} ModalProps={{ keepMounted: true }}>
    <header className="xp-library-header">
      <div>
        <span>Knowledge centre</span>
        <h2>Investor Library</h2>
        <p>Venture capital, fund operations, portfolio performance, and investor terminology.</p>
      </div>
      <div className="xp-library-header-actions">
        <Button onClick={downloadInvestorLibrary} className="xp-library-pdf">↓ PDF</Button>
        <button type="button" className="xp-library-close" onClick={onClose} aria-label="Close Investor Library">×</button>
      </div>
    </header>
    <section className="xp-library-search">
      <label htmlFor="xp-library-search">Search terms, acronyms, or definitions</label>
      <div><input id="xp-library-search" type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Try TVPI, capital call, carry..." />{search && <button type="button" onClick={() => setSearch("")} aria-label="Clear search">×</button>}</div>
    </section>
    <nav className="xp-library-categories" aria-label="Investor Library categories">
      {categories.map((item) => <button type="button" key={item} className={category === item ? "active" : ""} onClick={() => setCategory(item)}>{item}</button>)}
    </nav>
    <div className="xp-library-results"><span>{filteredTerms.length} {filteredTerms.length === 1 ? "term" : "terms"}</span>{(search || category !== "All") && <button type="button" onClick={clearFilters}>Clear filters</button>}</div>
    <main className="xp-library-list">
      {filteredTerms.map((item) => <article key={item.term}>
        <header><div><h3>{item.term}</h3>{item.aliases && <p>{item.aliases}</p>}</div><span>{item.category}</span></header>
        <p className="xp-library-definition">{item.definition}</p>
        {item.formula && <div className="xp-library-formula">{item.formula}</div>}
        <div className="xp-library-example"><strong>Example scenario</strong><p>{item.example}</p></div>
        {item.sourceUrl && <a href={item.sourceUrl} target="_blank" rel="noreferrer">Source: Investopedia ↗</a>}
      </article>)}
      {!filteredTerms.length && <div className="xp-library-empty"><strong>No matching terms</strong><p>Try a broader search or clear the category filter.</p><button type="button" onClick={clearFilters}>Clear filters</button></div>}
    </main>
  </Drawer>;
}
